import type {
  Handicap,
  LegType,
  LegTypes,
  MarketId,
  MarketStatus,
  MarketUpdate,
  Odds,
  OpportunityStatus,
  Runner,
  RunnerStatus,
  RunnerUpdate,
  Runners
} from '@ps/isp-web-services-types';
import { RunnerStatusMarket, RunnerStatusMarketDetails } from '@ps/isp-web-services-types';
import type { Draft } from 'immer';
import { isEqual } from 'lodash';

import { generateRunnerKey } from '@swf/sports/utils/generateRunnerKey';

// TODO: Update BaseMarket to be Market when https://jira.pyrsoftware.ca/jira/browse/GSFE-1262 is merged.
interface BaseMarket {
  marketStatus: MarketStatus;
  legTypes: LegTypes;
  runners: Runners;
}

interface MarketWithMarketId extends BaseMarket {
  marketId: MarketId;
}

interface MarketWithId extends BaseMarket {
  id: MarketId;
}

const isMarketWithMarketId = (
  market: MarketWithMarketId | MarketWithId
): market is MarketWithMarketId => 'marketId' in market;

export const haveOddsChanged = (oddsBefore: Odds | undefined, oddsAfter: Odds | undefined) =>
  oddsBefore?.trueOdds.decimalOdds?.decimalOdds !== oddsAfter?.trueOdds.decimalOdds?.decimalOdds;

export const hasMarketStatusChanged = (statusBefore?: MarketStatus, statusAfter?: MarketStatus) =>
  statusBefore !== statusAfter;

export const runnerStatusMap: Record<OpportunityStatus, RunnerStatusMarketDetails> = {
  AVAILABLE: RunnerStatusMarketDetails.ACTIVE,
  RESTRICTED: RunnerStatusMarketDetails.ACTIVE,
  SUSPENDED: RunnerStatusMarketDetails.SUSPENDED,
  UNAVAILABLE: RunnerStatusMarketDetails.REMOVED
};

// runnerStatus goes to 'REMOVED' temporarily before becoming undefined
const unavailableStatuses = [
  undefined,
  RunnerStatusMarket.REMOVED,
  RunnerStatusMarketDetails.REMOVED
];

export const hasRunnerStatusChanged = (statusBefore?: RunnerStatus, statusAfter?: RunnerStatus) => {
  const isRunnerStillUnavailable =
    unavailableStatuses.includes(statusBefore) && unavailableStatuses.includes(statusAfter);

  if (isRunnerStillUnavailable) {
    return false;
  }

  return statusBefore !== statusAfter;
};

export const hasRunnerHandicapChanged = (handicapBefore?: Handicap, handicapAfter?: Handicap) =>
  handicapBefore !== handicapAfter;

const updateRunnerProperties = (runner: Runner, update: RunnerUpdate) => {
  if (
    runner.winRunnerOdds?.decimalDisplayOdds?.decimalOdds !==
    update.winRunnerOdds?.decimalDisplayOdds?.decimalOdds
  ) {
    runner.winRunnerOdds = update.winRunnerOdds;
    runner.previousWinRunnerOdds = update.previousWinRunnerOdds;
  }
  runner.runnerStatus = update.runnerStatus as RunnerStatusMarketDetails;

  if (runner.handicap !== update.handicap) {
    runner.handicap = update.handicap;
  }
};

const updateRunners = (
  marketId: MarketId,
  existingRunners: Draft<Runner[]> | undefined,
  updatedRunners: RunnerUpdate[]
) => {
  existingRunners?.forEach((existingRunner: Runner) => {
    const updatedRunner = updatedRunners.find(
      (updateRunner) =>
        generateRunnerKey(updateRunner.runnerId) ===
        generateRunnerKey({
          marketId,
          selectionId: existingRunner.selectionId
        })
    );
    if (updatedRunner) {
      updateRunnerProperties(existingRunner, updatedRunner);
    } else {
      existingRunner.winRunnerOdds = undefined;
    }
  });
};

const updateMarket = (
  market: Draft<MarketWithMarketId | MarketWithId>, // TODO: This can become Market when https://github.com/Flutter-International-Sports-Platforms/web-bff/pull/116 is merged
  marketUpdate: MarketUpdate
) => {
  market.marketStatus = marketUpdate.marketStatus;

  // Only update the legTypes if they are different.
  // This will prevent unnecessary rerenders by keeping the reference to the original legTypes array.
  if (!isEqual(market.legTypes, marketUpdate.legTypes)) {
    market.legTypes = marketUpdate.legTypes as LegType[];
  }

  updateRunners(
    isMarketWithMarketId(market) ? market.marketId : market.id,
    market.runners,
    marketUpdate.runners
  );
};

export default updateMarket;
